'use client';

import { Typography } from '@mui/material';
import { type StructuredContent } from '@repo/supergraph/structuredContent/types';
import { ElementType } from 'react';
import { Accordion } from './components/Accordion';
import { AccordionItem } from './components/AccordionItem';
import { AnchorLink } from './components/AnchorLink';
import { AnchorLinkTarget } from './components/AnchorLinkTarget';
import { BackToTopLink } from './components/BackToTopLink';
import { BainGenericContainer } from './components/BainGenericContainer';
import { BankingAttentionNote } from './components/BankingAttentionNote';
import { BankingWhatTheNerdsThink } from './components/BankingWhatTheNerdsThink';
import { Button } from './components/Button';
import { Citation } from './components/Citation';
import type { ICitation } from './components/Citation/context';
import { Column } from './components/Columns/Column';
import { Columns } from './components/Columns/Columns';
import { Container } from './components/Container';
import { ContentHeading } from './components/ContentHeading';
import { ContentImage } from './components/ContentImage';
import { ContentRibbon } from './components/ContentRibbon';
import { ContentSection } from './components/ContentSection';
import { Divider } from './components/Divider';
import { DocumentStructuredContent } from './components/Document';
import CalculatedFieldsForm from './components/Embeds/CalculatedFieldsForm';
import Infogram from './components/Embeds/Infogram';
import { ExpandCard } from './components/ExpandCard';
import { FirstPersonPerspective } from './components/FirstPersonPerspective';
import { FirstPersonPerspectiveItem } from './components/FirstPersonPerspectiveItem';
import { IconCardList } from './components/IconCardList';
import { Li } from './components/Li';
import { Link } from './components/Link';
import { List } from './components/List';
import { MarketplaceField } from './components/MarketplaceField';
import { MiniProductCard } from './components/MiniProductCard';
import { NerdTip } from './components/NerdTip';
import { NerdyTip } from './components/NerdyTip';
import { NWVerifiedContainer } from './components/NWVerifiedContainer';
import { SMBIllustrationCardList } from './components/SMBIllustrationCardList';
import { SMBInfoGatheringCta } from './components/SMBInfoGatheringCta';
import { SMBProsAndConsTable } from './components/SMBProsAndConsTable';
import {
  SMBWhyTrustNerdWallet,
  SMBWhyTrustNerdWalletBullet,
} from './components/SMBWhyTrustNerdWallet';
import { Spacer } from './components/Spacer';
import { StarRating } from './components/StarRating';
import { StarRatingCategories } from './components/StarRatingCategories';
import { TableOfContents } from './components/TableOfContents';
import { populateTableOfContents } from './components/TableOfContents/utils';
import { Type } from './components/Type';
import { Well } from './components/Well';
import { WPBlock } from './components/WPBlock';
import { WPDynamicPlacement } from './components/WPDynamicPlacement';
import { WPHouseAd } from './components/WPHouseAd';
import { WPTable } from './components/WPTable';
import { WPVideo } from './components/WPVideo';
import { NodeContextProvider } from './NodeContext';
import {
  groupedByH2ContentAndWrapItWithContainer,
  injectRatingIntoStarRatingCategories,
} from './utils';

const components: Record<string, ElementType> = {
  Accordion,
  AccordionItem,
  AnchorLink,
  AnchorLinkTarget,
  BackToTopLink,
  BainGenericContainer,
  Button,
  CalculatedFieldsForm,
  Citation,
  Columns,
  Column,
  Container,
  ContentHeading,
  ContentImage,
  ContentSection,
  ContentRibbon,
  Divider,
  Document: DocumentStructuredContent,
  ExpandCard,
  // Experiment,
  FirstPersonPerspective,
  FirstPersonPerspectiveItem,
  IconCardList,
  Infogram,
  Li,
  Link,
  List,
  MarketplaceField,
  MiniProductCard,
  NerdTip,
  NWVerifiedContainer,
  SMBIllustrationCardList,
  NerdyTip,
  SMBInfoGatheringCta,
  SMBProsAndConsTable,
  SMBWhyTrustNerdWallet,
  SMBWhyTrustNerdWalletBullet,
  Spacer,
  StarRating,
  StarRatingCategories,
  TableOfContents,
  Type,
  Well,
  BankingWhatTheNerdsThink,
  BankingAttentionNote,
  WPBlock,
  WPDynamicPlacement,
  WPHouseAd,
  Video: WPVideo,
  WPTable,
};

export const StructuredContentRenderer = ({
  doc,
  tableOfContentsEnabled = false,
  overallRating,
  excludeHeadings,
}: {
  doc: StructuredContent;
  tableOfContentsEnabled?: boolean;
  overallRating?: number;
  excludeHeadings?: string[];
}) => {
  if (typeof doc === 'string') {
    return <Typography>{doc}</Typography>;
  }

  let normalizedDoc: StructuredContent = populateTableOfContents(doc);
  if (doc && tableOfContentsEnabled) {
    normalizedDoc = groupedByH2ContentAndWrapItWithContainer(doc, excludeHeadings);
  }

  if (overallRating) {
    normalizedDoc = injectRatingIntoStarRatingCategories(normalizedDoc, overallRating);
  }

  return (
    <>
      {[normalizedDoc].flat().map((child, idx) => {
        if (typeof child === 'string')
          return (
            <Typography key={`sc-doc-text-${idx}`} component="span">
              {child}
            </Typography>
          );

        if (!child) {
          return null;
        }

        const Component = components[child.type];
        if (child.type === 'Citation') {
          const timestamp = new Date().getTime(); // this is used to calculate which citation comes first (replacing SC NodePath)

          return (
            <NodeContextProvider timestamp={timestamp} key={`citation_${idx}`}>
              <Citation {...(child.props as unknown as ICitation)} />
            </NodeContextProvider>
          );
        }
        if (Component) {
          if (child.type === 'AccordionItem') {
            return <Component {...child} key={idx} structuredContentChildIndex={idx} />;
          }
          return <Component {...child} key={idx} />;
        }
        console.warn(
          `Structured Content Renderer missing component for type ${child.type}`,
          child.props,
        );
        return null;
      })}
    </>
  );
};
